import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@workos-inc/authkit-react";
import { Settings, LogOut, PanelRightClose, PanelRightOpen, Megaphone, BookOpen } from "lucide-react";
// TODO: re-enable when admin role switching is wired up
// import { CircleHelp, MessageSquare } from "lucide-react";
// import { Tabs, TabsList, TabsTrigger } from "@/v2/components/ui/tabs";
import { BrandLogo, BrandLogoFull } from "@/v2/components/BrandLogo";
import { Avatar, AvatarFallback } from "@/v2/components/ui/avatar";
import { Popover, PopoverTrigger, PopoverContent } from "@/v2/components/ui/popover";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/v2/components/ui/tooltip";
import { cn } from "@/v2/lib/utils";
import { useNewUpdates } from "@/v2/hooks/useNewUpdates";
import type { NavItem } from "./MobileBottomNav";

interface SidebarProps {
	navItems: NavItem[];
	userInitials: string;
	userDisplayName: string;
	userEmail?: string;
	showRoleSwitcher?: boolean;
	currentRole?: "attorney" | "admin";
	onRoleSwitch?: () => void;
	collapsed: boolean;
	onToggleCollapse: () => void;
}

export function Sidebar({
	navItems,
	userInitials,
	userDisplayName,
	userEmail,
	showRoleSwitcher: _showRoleSwitcher,
	currentRole: _currentRole = "attorney",
	onRoleSwitch: _onRoleSwitch,
	collapsed,
	onToggleCollapse,
}: SidebarProps) {
	const navigate = useNavigate();
	const location = useLocation();
	const { signOut } = useAuth();
	const nav = (path: string) => navigate(path + location.search);
	const { hasNew } = useNewUpdates();

	const menuItems = [
		{ label: "Settings", icon: Settings, path: "/settings" },
		/* hourglass:mobile:v0 — disabled for mobile v0
		{ label: "Support", icon: MessageSquare, path: "/support" },
		{ label: "FAQ", icon: CircleHelp, path: "/faq" },
		*/
	];

	const utilityItems = [
		{ label: "Documentation", icon: BookOpen, path: "/docs", hasNew: false },
		{ label: "Updates", icon: Megaphone, path: "/updates", hasNew },
	];

	return (
		<TooltipProvider>
		<aside
			className={cn(
				"flex h-dvh flex-col border-r border-border bg-card transition-[width] duration-200",
				collapsed ? "w-16" : "w-60",
			)}
		>
			{/* Logo + collapse toggle */}
			<div className={cn("flex items-center pt-5 pb-2", collapsed ? "justify-center px-2" : "justify-between px-5")}>
				{collapsed ? (
					<BrandLogo className="h-7 w-7 text-foreground" aria-label="Hourglass" />
				) : (
					<BrandLogoFull className="h-7 w-auto text-foreground" aria-label="Hourglass" />
				)}
				{!collapsed && (
					<button
						onClick={onToggleCollapse}
						className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
					>
						<PanelRightOpen className="size-5" />
					</button>
				)}
			</div>

			{/* hourglass:mobile:v0 — role switcher disabled for mobile v0
			{showRoleSwitcher && !collapsed && (
				<div className="px-3 py-3">
					<Tabs
						value={currentRole}
						onValueChange={(value) => {
							if (value !== currentRole) onRoleSwitch?.();
						}}
					>
						<TabsList>
							<TabsTrigger value="attorney">Timekeeper</TabsTrigger>
							<TabsTrigger value="admin">Admin</TabsTrigger>
						</TabsList>
					</Tabs>
				</div>
			)}
			*/}

			{/* Nav items */}
			<nav className={cn("flex-1 py-2", collapsed ? "px-2" : "px-3")}>
				<div className="flex flex-col gap-1">
					{navItems.map((item) => {
						const isActive = location.pathname.startsWith(item.path);
						return collapsed ? (
							<Tooltip key={item.path}>
								<TooltipTrigger asChild>
									<button
										onClick={() => nav(item.path)}
										className={cn(
											"flex items-center justify-center rounded-lg p-2.5 transition-colors",
											isActive
												? "bg-accent text-accent-foreground"
												: "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
										)}
									>
										<item.icon className="size-5" />
									</button>
								</TooltipTrigger>
								<TooltipContent>{item.label}</TooltipContent>
							</Tooltip>
						) : (
							<button
								key={item.path}
								onClick={() => nav(item.path)}
								className={cn(
									"flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
									isActive
										? "bg-accent text-accent-foreground"
										: "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
								)}
							>
								<item.icon className="size-5" />
								{item.label}
							</button>
						);
					})}
				</div>
			</nav>

			{/* Utility items: Documentation + Updates */}
			<div className={cn("pb-1 opacity-70", collapsed ? "px-2" : "px-3")}>
				<div className="flex flex-col gap-0.5">
					{utilityItems.map((item) => {
						const isActive = location.pathname.startsWith(item.path);
						return collapsed ? (
							<Tooltip key={item.path}>
								<TooltipTrigger asChild>
									<button
										onClick={() => nav(item.path)}
										className={cn(
											"relative flex items-center justify-center rounded-lg p-2.5 transition-colors",
											isActive
												? "bg-accent text-accent-foreground"
												: "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
										)}
									>
										<item.icon className="size-4" />
										{item.hasNew && (
											<span className="absolute top-1.5 right-1.5 size-1.5 rounded-full bg-primary" />
										)}
									</button>
								</TooltipTrigger>
								<TooltipContent>{item.label}</TooltipContent>
							</Tooltip>
						) : (
							<button
								key={item.path}
								onClick={() => nav(item.path)}
								className={cn(
									"relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
									isActive
										? "bg-accent text-accent-foreground"
										: "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
								)}
							>
								<item.icon className="size-4" />
								{item.label}
								{item.hasNew && (
									<span className="ml-auto size-1.5 rounded-full bg-primary" />
								)}
							</button>
						);
					})}
				</div>
			</div>

			{/* Expand button when collapsed */}
			{collapsed && (
				<div className="px-2 py-2">
					<button
						onClick={onToggleCollapse}
						className="flex w-full items-center justify-center rounded-lg p-2.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
					>
						<PanelRightClose className="size-5" />
					</button>
				</div>
			)}

			{/* User profile */}
			<div className={cn("border-t border-border", collapsed ? "p-2" : "p-3")}>
				<Popover>
					<PopoverTrigger asChild>
						{collapsed ? (
							<button className="flex w-full items-center justify-center rounded-lg p-2 hover:bg-accent transition-colors">
								<Avatar className="h-8 w-8">
									<AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
										{userInitials}
									</AvatarFallback>
								</Avatar>
							</button>
						) : (
							<button className="flex w-full items-center gap-3 rounded-lg px-3 py-2 hover:bg-accent transition-colors">
								<Avatar className="h-8 w-8">
									<AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
										{userInitials}
									</AvatarFallback>
								</Avatar>
								<div className="flex-1 text-left min-w-0">
									<p className="text-sm font-medium truncate">{userDisplayName}</p>
									{userEmail && (
										<p className="text-xs text-muted-foreground truncate">{userEmail}</p>
									)}
								</div>
							</button>
						)}
					</PopoverTrigger>
					<PopoverContent side={collapsed ? "right" : "top"} align={collapsed ? "end" : "start"} sideOffset={8}>
						<div className="flex flex-col gap-0.5">
							{menuItems.map((item) => {
								const isActive = location.pathname === item.path;
								return (
									<button
										key={item.path}
										onClick={() => nav(item.path)}
										className={cn(
											"flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors",
											isActive
												? "bg-accent text-accent-foreground"
												: "hover:bg-accent",
										)}
									>
										<item.icon className="size-4" />
										{item.label}
									</button>
								);
							})}
							<div className="my-1 h-px bg-border" />
							<button
								onClick={() => signOut({ returnTo: window.location.origin })}
								className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors hover:bg-accent"
							>
								<LogOut className="size-4" />
								Sign out
							</button>
						</div>
						{/* hourglass:mobile:v0 — role switcher disabled for mobile v0
						{showRoleSwitcher && collapsed && (
							<Tabs
								className="mt-2"
								value={currentRole}
								onValueChange={(value) => {
									if (value !== currentRole) onRoleSwitch?.();
								}}
							>
								<TabsList>
									<TabsTrigger value="attorney">Timekeeper</TabsTrigger>
									<TabsTrigger value="admin">Admin</TabsTrigger>
								</TabsList>
							</Tabs>
						)}
						*/}
					</PopoverContent>
				</Popover>
			</div>
		</aside>
		</TooltipProvider>
	);
}
